/* Nafe Logistica offline queue — persists mutations in IndexedDB while offline. */

import { setAccessToken } from "./api";

const DB_NAME = "nafe-offline";
const STORE = "queue";
const VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const result = fn(store);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
  });
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function serializeItem(item) {
  // item: { url, method, headers, body: FormData | plain object }
  if (item.body instanceof FormData) {
    const parts = [];
    for (const [k, v] of item.body.entries()) {
      if (v instanceof Blob) {
        const buf = await v.arrayBuffer();
        parts.push({ k, blob: buf, name: v.name || "file", type: v.type || "application/octet-stream" });
      } else {
        parts.push({ k, v });
      }
    }
    return { ...item, body: { __formdata: true, parts } };
  }
  return item;
}

function deserializeItem(item) {
  if (item.body && item.body.__formdata) {
    const fd = new FormData();
    for (const p of item.body.parts) {
      if (p.blob) {
        fd.append(p.k, new Blob([p.blob], { type: p.type }), p.name);
      } else {
        fd.append(p.k, p.v);
      }
    }
    return { ...item, body: fd };
  }
  return item;
}

export async function enqueue(item) {
  if (item.url === "/tracking/ping") {
    const existing = await listQueue();
    for (const old of existing) {
      if (old.url === "/tracking/ping") await removeFromQueue(old.id);
    }
  }

  const record = { id: newId(), created_at: Date.now(), ...(await serializeItem(item)) };
  await tx("readwrite", (s) => s.put(record));
  // Best-effort Background Sync registration so the OS/browser can replay
  // even if the page is closed (Chromium only; Safari falls back to
  // our online-event auto-sync in OfflineBar).
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.ready;
      if ("sync" in reg) await reg.sync.register("nafe-sync");
    }
  } catch { /* ignore */ }
  return record.id;
}

export async function listQueue() {
  return new Promise(async (resolve) => {
    const db = await openDB();
    const t = db.transaction(STORE, "readonly");
    const s = t.objectStore(STORE);
    const req = s.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

export async function removeFromQueue(id) {
  await tx("readwrite", (s) => s.delete(id));
}

function authHeaders(extra = {}) {
  const headers = { ...extra };
  try {
    const token = localStorage.getItem("nafe_access_token");
    if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;
  } catch {
    
   }
  return headers;
}

async function tryRefreshToken(baseUrl) {
  try {
    const res = await fetch(`${baseUrl}/auth/refresh`, { method: "POST", credentials: "include" });
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.token) {
      setAccessToken(data.token);
      return true;
    }
  } catch {
    
  }
  return false;
}

function buildFetchOpts(it) {
  const baseHeaders = authHeaders(it.headers || {});
  return {
    method: it.method || "POST",
    credentials: "include",
    headers: it.body instanceof FormData
      ? (baseHeaders.Authorization ? { Authorization: baseHeaders.Authorization } : undefined)
      : { "Content-Type": "application/json", ...baseHeaders },
    body: it.body instanceof FormData ? it.body : JSON.stringify(it.body),
  };
}

async function replayItem(baseUrl, raw) {
  const it = deserializeItem(raw);
  const res = await fetch(`${baseUrl}${it.url}`, buildFetchOpts(it));
  if (res.ok) {
    await removeFromQueue(raw.id);
    return "ok";
  }
  if (res.status === 401 || res.status === 403) return "auth";
  return "fail";
}

async function collapseTrackingPings() {
  const items = await listQueue();
  const pings = items.filter((i) => i.url === "/tracking/ping");
  if (pings.length <= 1) return;
  pings.sort((a, b) => b.created_at - a.created_at);
  for (const old of pings.slice(1)) await removeFromQueue(old.id);
}

export async function processQueue(baseUrl) {
  await collapseTrackingPings();
  const items = await listQueue();
  let ok = 0, fail = 0, authFailed = false;
  let refreshed = false;

  for (const raw of items) {
    try {
      let outcome = await replayItem(baseUrl, raw);
      if (outcome === "auth" && !refreshed) {
        refreshed = await tryRefreshToken(baseUrl);
        if (refreshed) outcome = await replayItem(baseUrl, raw);
      }
      if (outcome === "ok") ok++;
      else if (outcome === "auth") {
        authFailed = true;
        fail += items.length - ok - fail;
        break;
      } else fail++;
    } catch {
      fail++;
    }
  }

  return { ok, fail, remaining: (await listQueue()).length, authFailed };
}

export async function purgeQueueUrl(url) {
  const items = await listQueue();
  for (const item of items) {
    if (item.url === url) await removeFromQueue(item.id);
  }
}

export async function queueSize() {
  return (await listQueue()).length;
}
